import * as React from 'react';
import * as _ from 'lodash';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { RoleTableRowData } from '../types';
import * as css from './RolesList.scss';
import { TeamMembersByRoles, InviteData, UserData } from '../../types/teamMembers';
import TeammatesPopover from '../TeammatesPopover/TeammatesPopover';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { connector } from '../../components/RolesListPage/RolesListContainer';
import RolesActions from '../RolesActions/RolesActions';
import {
  reportDeleteRoleClick,
  reportDuplicateAndEditClick,
  reportEditCustomRole,
  reportManageRolesLoad,
  reportViewRoleClick
} from '../../redux/actions/bi.actions';
import { getRoles } from '../../redux/selectors/roles';
import { getAreasWithAssignableRoles } from '../../redux/selectors/roleAreas';
import { getIsSite } from '../../redux/selectors/config';
import { getInvitePeoplePermission } from '../../redux/selectors/teamDetails';
import { withExperiments, InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { Card, Table, Text, TextButton } from 'wix-style-react';
import { State } from '../../redux/configure-store';
import { getEnforceContributors } from '../../redux/actions/serverApi.actions';

const getColumnCursor = (area, shouldHidePredefinedRolesViewOrDuplicate) => {
  if (area?.roles[0]?.isCustom ?? !shouldHidePredefinedRolesViewOrDuplicate) {
    return 'pointer';
  } else {
    return 'default';
  }
};

interface IProps extends InjectedTranslateProps, InjectedExperimentsProps, ConnectedProps<typeof connector> {}

const RolesList: React.FC<IProps> = ({ t, experiments, onRoleSelected, usersByRoles, invitesByRoles, onRemoveRequest, isOwner, onCannotRemoveRole }) => {

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<any>();

  const areas = useSelector((state: State) => getAreasWithAssignableRoles(state, experiments));

  const roles = useSelector(getRoles);

  const isSite = useSelector(getIsSite);

  const shouldHidePredefinedRolesViewOrDuplicate = useMemo(() => !experiments?.enabled('specs.ident.PredefinedRolesScopes'), [experiments]);

  const canInvitePeoplePermission = useSelector(getInvitePeoplePermission);

  useEffect(() => {
    dispatch(reportManageRolesLoad(location?.state?.origin));
  }, [location, dispatch]);

  const onDetailsCellClick = useCallback((rowData) => {
    const { id, isCustom } = rowData;
    if (isCustom) {
      history.push(`/role/${id}/edit`, { origin: '/roles' });
      dispatch(reportEditCustomRole(id));
    } else if (!shouldHidePredefinedRolesViewOrDuplicate) {
      history.push(`role/${id}`, { origin: '/roles' });
      dispatch(reportViewRoleClick(id));
    }
  }, [history, dispatch]);

  const onRemoveRoleClick = useCallback((roleData: RoleTableRowData) => {
    const usersWithRole = usersByRoles[roleData.id] ? usersByRoles[roleData.id].length : 0;
    const invitesWithRole = invitesByRoles[roleData.id] ? invitesByRoles[roleData.id].length : 0;
    if (usersWithRole || invitesWithRole) {
      onCannotRemoveRole(roleData, usersWithRole + invitesWithRole, roles);
    } else {
      onRemoveRequest(roleData);
    }
    dispatch(reportDeleteRoleClick(roleData.id));
  }, [usersByRoles, invitesByRoles, onCannotRemoveRole, onRemoveRequest, dispatch]);

  const reportDuplicateAndEditAction = useCallback((roleId) => {
    dispatch(reportDuplicateAndEditClick(location?.pathname, roleId));
  }, [location, dispatch]);

  const onInviteRoleClick = useCallback((id) => {
    dispatch(getEnforceContributors( { origin: '/roles', roleIds: [id] }));
  }, []);

  const onRoleSelectedCurried = useCallback((selectedRole) => onRoleSelected(selectedRole), []);

  return <>
    {
      areas.map(area => (
        area.roles?.length > 0 &&
        <Card className={css.card} key={`${area.title}-${area.roles.map(role => role.id).join('-')}`}>
          <Card.Header title={area.title}/>
          <Card.Divider />
          <Table
              data-hook="roles-table"
              data={area.roles}
              rowVerticalPadding="medium"
              rowClass={css.row}
              columns={[
                { title: 'details', render: detailsCell(), style: { paddingRight: '18px', cursor: getColumnCursor(area, shouldHidePredefinedRolesViewOrDuplicate) } },
                { title: 'teammates', render: teamMatesCell(usersByRoles, invitesByRoles, onRoleSelectedCurried, t), width: '100px', style: { cursor: getColumnCursor(area, shouldHidePredefinedRolesViewOrDuplicate) } },
                { title: 'edit', render: actionsCell(onRemoveRoleClick, reportDuplicateAndEditAction, isOwner, isSite, canInvitePeoplePermission, onInviteRoleClick, onDetailsCellClick, shouldHidePredefinedRolesViewOrDuplicate), width: '200px' , style: { cursor: getColumnCursor(area, shouldHidePredefinedRolesViewOrDuplicate) } }
              ]}
              onRowClick={onDetailsCellClick}
          >
              <Table.Content titleBarVisible={false}/>
          </Table>
        </Card>
      ))
    }
  </>;
};

const detailsCell = () => (rowData: RoleTableRowData) => {
  return (
    <div className={css.detailCell} data-hook="roles-list-details-cell">
      <Text size="medium" weight="normal" tagName="div" data-hook="roles-list-details-name">{rowData.title}</Text>
      <Text size="small" weight="thin" tagName="div" data-hook="roles-list-details-description">{rowData.description}</Text>
    </div>
  );
};

const teamMatesCell = (usersByRoles: TeamMembersByRoles<UserData>, invitesByRoles: TeamMembersByRoles<InviteData>, onRoleSelected, t) => (rowData: RoleTableRowData) => {
  // Safely getting the amount of teammates under this role
  const amount = _.get(usersByRoles, rowData.id, []).length + _.get(invitesByRoles, rowData.id, []).length;
  if (!amount) {
    return (<div data-hook="roles-list-teammates-cell" onClick={e => e.stopPropagation()}>
      <Text className={css.teamMatesCell} secondary={true} skin="disabled">
        {t('rolesDetails.list.cells.notAssigned')}
      </Text>
    </div>);
  }

  return (
    <div data-hook="roles-list-teammates-cell" onClick={e => e.stopPropagation()}>
      <TeammatesPopover users={usersByRoles[rowData.id]} invites={invitesByRoles[rowData.id]} seeAllPath="/" onClick={onRoleSelected} role={rowData.id}>
        <TextButton className={css.teamMatesCell} dataHook="roles-list-teammates-button">
          {t(`rolesDetails.list.cells.teammates.${amount > 1 ? 'plural' : 'singular'}`, { count: amount })}
        </TextButton>
      </TeammatesPopover>
    </div>
  );
};

const actionsCell = (onRemoveRequest, reportDuplicateAndEditAction, isOwner, isSite, canInvitePeoplePermission, onInviteRoleClick, onDetailsCellClick, shouldHidePredefinedRolesViewOrDuplicate) => (rowData: RoleTableRowData) => {
  return (
    <div data-hook="roles-list-actions-cell" className={css.roleCell}>
      <RolesActions rowData={rowData} isOwner={isOwner} isSite={isSite} onRemoveRequest={onRemoveRequest} reportDuplicateAndEditAction={reportDuplicateAndEditAction} canInvitePeoplePermission={canInvitePeoplePermission} onInviteRoleClick={onInviteRoleClick} onEditOrViewClick={onDetailsCellClick} shouldHidePredefinedRolesViewOrDuplicate={shouldHidePredefinedRolesViewOrDuplicate}/>
    </div>
  );
};

export default withExperiments(translate()(RolesList));
